/**
 * Lane FX-PORT: the user-approved skill-FX samples (tools/fxSamples/skills.html + taiji.html, docs/art/liked/
 * fx-samples-2026-10-11/) played in the game for the moves they were made for. Each port file (render/fx/port*.ts)
 * names its moves and paints with the sample's own drawing (render/fx/sampleArt/skills.ts, taiji.ts); this runtime
 * gives that drawing a scene from the live fight:
 * - the sample's clock: the wind-up (a cast) plays the sample's gathering span; the release plays its lead into the
 *   blow compressed into the motion's contact time, then runs 1:1 (12 fps held frames);
 * - the caster's and the foe's feet (world points projected every frame, so the camera can move), the weapon tip and
 *   hands from the frame the renderer drew (the rig's blade lines), the chest from the figure's height;
 * - a foe on the left: the sample is drawn mirrored (every sample is staged left to right);
 * - two layers like the page: `under` on the ground under the figures, `over` above them.
 * Each layer is painted once per 12 fps step through a private pixel pen into a pooled offscreen canvas (outline, ramps,
 * dither baked in) and blitted on the display frames between (the cost: one pen pass per step, one picture per frame).
 * What the move would draw otherwise (its ink or legacy marks, the shared slash and sparks on its blows) is dropped;
 * sounds, floats, the callout, the cinematic, and the split-body afterimages ('ghost') stay. In a duel bout a plain
 * blow keeps the small spark (fx/duelHits.ts) and the sample's hit stars are left out (`quiet`).
 * Wired from the registry with no fx ids: the factory wraps the Effects' `handle` on its first step, so it sits
 * outside every other wrapper (fx/duelHits.ts) whatever the load order.
 */
import type { Entity, World, WorldEvent } from '@nine/shared';
import { SKILLS } from '@nine/shared';
import { registerRenderPass } from '../../passes/registry';
import { touchCanvas } from '../../pixel';
import { bladesIn } from '../../rig/sets';
import { SKILL_SWINGS } from '../../skillFx';
import { viewOf, visualScale } from '../../sprites';
import { PixelPen } from '../pixelRules';
import { registerFx, type FxHost } from '../registry';
import type { V } from './kit';

/** What a port paints with: the sample's scene, in the sample's own (left to right) space. */
export interface PortScene {
  t: number;
  a: V;
  f: V;
  d: V;
  chest: number;
  body(t: number): V;
  limbs(t: number): { hand: V; handB: V; tip: V };
  hands(t: number): [V, V];
  quiet: boolean;
  top: number;
}

export interface PortSpec {
  /** The sample's id (for the lane notes). */
  id: string;
  /** The game moves (SkillDef ids) that play it. */
  skills: readonly string[];
  /** On in the game (main turns a port on once it is reviewed); off, the move keeps its old effect. */
  on?: boolean;
  /** Sample time the effect starts at when the move comes out with no wind-up. */
  pre: number;
  /** Sample time of the blow (lined up with the motion's contact). */
  hit: number;
  /** Sample time everything is gone. */
  end: number;
  /** The span played over a wind-up (cast), if the sample gathers before the blow. */
  cast?: readonly [number, number];
  /** The big blow (a 1-px stage jolt in the sample: a small camera shake here). */
  big?: number;
  /** The sample's full dash or leap length (art px): the caster is drawn from where its dash started. */
  dash?: number;
  /** The sample's offset of the body off its start at time t (`Sample.move`), for a dash or leap. */
  move?(s: { a: V; f: V; d: V; chest: number }, t: number): V;
  /** Sample times to leave a split-body afterimage at (the locked look), stepping back toward where the body came from. */
  ghosts?: readonly number[];
  /** Art px the body stepped in over the ghosts (taiji: 12). */
  stepIn?: number;
  under?(g: CanvasRenderingContext2D, s: PortScene): void;
  over(g: CanvasRenderingContext2D, s: PortScene): void;
  /** A long move's port: the sample time from the move's own beats (render/longMove.ts `longRetime`); null keeps this clock. */
  retime?: (srcId: number) => number | null;
}

const PORTS = new Map<string, PortSpec>();
/** Registers a ported sample for its moves (once each). */
export function registerPort(spec: PortSpec): void {
  for (const id of spec.skills) {
    if (PORTS.has(id)) throw new Error(`fx port for '${id}' registered twice`);
    PORTS.set(id, spec);
  }
}
export const portOf = (skillId: string): PortSpec | undefined => PORTS.get(skillId);

/** A port plays when its spec is on, or every port when forced on for a review capture (dev: `window.__fxPortOn = true`). */
const portOn = (spec: PortSpec): boolean =>
  !!spec.on || (!!import.meta.env?.DEV && typeof window !== 'undefined' && !!(window as unknown as { __fxPortOn?: boolean }).__fxPortOn);
const FPS = 12;
/** Offscreen layer size, art px (the sample stage is 200 x 136; the game's foes stand further apart). */
const BOX_W = 420;
const BOX_H = 240;
const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1]];

// ── Cached layers ──

const pen = new PixelPen();
const pool: HTMLCanvasElement[] = [];
function takeCanvas(res: number): HTMLCanvasElement {
  const c = pool.pop() ?? document.createElement('canvas');
  if (c.width !== BOX_W * res || c.height !== BOX_H * res) {
    c.width = BOX_W * res;
    c.height = BOX_H * res;
  }
  return c;
}

interface Layer {
  c: HTMLCanvasElement | null;
  step: number;
  /** The box's top-left in screen art px (no view offset), as painted. */
  bx: number;
  by: number;
  res: number;
}

// ── One move playing ──

interface Sample {
  T: number;
  hand: V;
  handB: V;
  tip: V;
}

interface Play {
  spec: PortSpec;
  srcId: number;
  skillId: string;
  tgtId: number | null;
  phase: 'cast' | 'release';
  c0: number;
  windup: number;
  r0: number;
  contact: number;
  fromCast: boolean;
  /** World points: the caster's start, the foe (live until the blow), where a dash ended. */
  aW: [number, number];
  fW: [number, number];
  dashed: boolean;
  quiet: boolean;
  mirror: boolean | null;
  hist: Sample[];
  ghostsLeft: number[];
  bigDone: boolean;
  under: Layer;
  over: Layer;
  done: boolean;
}

type Host = FxHost & {
  handle(ev: WorldEvent): void;
  flash: { col: string; a: number };
  punch: number;
};

/** The two fighters of a bout under way (as fx/duelHits.ts reads it). */
function boutPair(w: World): [number, number] | null {
  const pb = w.pvpBout;
  if (pb?.fight) return [pb.a, pb.b];
  const d = w.duel as { phase?: string; foeId?: number } | null;
  if (d && d.phase === 'fight' && typeof d.foeId === 'number' && d.foeId >= 0) return [w.player.id, d.foeId];
  return null;
}

class Runner {
  private plays: Play[] = [];
  private dashFrom = new Map<number, [number, number]>();
  private wrapped = false;

  constructor(private readonly host: Host) {
    // Review captures (scripts/lanes/fx-port/game-gif.mjs): the sample clock of the move playing.
    if (import.meta.env?.DEV && typeof window !== 'undefined')
      (window as unknown as { __fxPortT: () => number | null }).__fxPortT = () => {
        const p = this.plays.find((q) => !q.done);
        return p ? this.clock(p) : null;
      };
    if (import.meta.env?.DEV && typeof window !== 'undefined')
      (window as unknown as { __fxPortDbg: () => unknown }).__fxPortDbg = () =>
        this.plays.map((p) => {
          const src = this.host.world.get(p.srcId);
          const count = (c: HTMLCanvasElement | null) => {
            if (!c) return -1;
            const d = c.getContext('2d')!.getImageData(0, 0, c.width, c.height).data;
            let n = 0;
            for (let i = 3; i < d.length; i += 4) if (d[i]) n++;
            return n;
          };
          const url = (c: HTMLCanvasElement | null) => c?.toDataURL() ?? null;
          return { urlU: url(p.under.c), urlO: url(p.over.c), T: this.clock(p), feet: src ? this.feet(src) : null, under: { ...p.under, c: count(p.under.c) }, over: { ...p.over, c: count(p.over.c) }, mirror: p.mirror, fW: p.fW, aW: p.aW };
        });
  }

  step(): void {
    if (!this.wrapped) this.wrap();
    const w = this.host.world;
    for (const p of this.plays) {
      const src = w.get(p.srcId);
      if (!src || src.dead) p.done = true;
      else if (p.phase === 'cast' && (!src.cast || src.cast.skillId !== p.skillId) && w.time - p.c0 > 0.05) p.done = true;
      if (p.done || !src) continue;
      const T = this.clock(p);
      if (T >= p.spec.end) {
        p.done = true;
        continue;
      }
      if (p.phase === 'release' && T < p.spec.hit) {
        const tgt = w.get(p.tgtId);
        if (tgt && !tgt.dead) p.fW = [tgt.x, tgt.y];
      }
      this.sampleLimbs(p, src, T);
      this.leaveGhosts(p, src, T);
      if (p.spec.big !== undefined && !p.bigDone && T >= p.spec.big) {
        p.bigDone = true;
        this.host.shake = Math.max(this.host.shake, 1.5);
      }
    }
    for (const p of this.plays)
      if (p.done) {
        for (const l of [p.under, p.over])
          if (l.c) {
            pool.push(l.c);
            l.c = null;
          }
      }
    this.plays = this.plays.filter((p) => !p.done);
  }

  /** Wraps the Effects' event handling (outermost: done on the first step, after every factory ran). */
  private wrap(): void {
    this.wrapped = true;
    const host = this.host;
    const inner = host.handle.bind(host);
    host.handle = (ev: WorldEvent): void => {
      if (ev.type === 'skillDash') this.dashFrom.set(ev.id, [ev.fromX, ev.fromY]);
      const id = ev.type === 'castStart' || ev.type === 'skill' || ev.type === 'hit' ? ev.skillId : null;
      const spec = id ? PORTS.get(id) : undefined;
      if (!spec || !portOn(spec)) return inner(ev);
      if (ev.type === 'hit') return this.onHit(ev, inner);
      // A cast or a release: the move's own marks give way to the sample's.
      const fx0 = host.fx.length;
      const parts0 = host.parts.length;
      const flash = host.flash;
      const flashA = flash.a;
      const punch0 = host.punch;
      inner(ev);
      const added = host.fx.splice(fx0);
      for (const f of added) if (f.type === 'ghost') host.fx.push(f);
      host.parts.length = Math.min(host.parts.length, parts0);
      host.flash = flash;
      flash.a = flashA;
      host.punch = punch0;
      // A wind-up the sample has no span for shows nothing of its own (the sample starts on the release).
      if (ev.type === 'castStart') {
        if (spec.cast) this.startCast(spec, ev.srcId, ev.skillId);
      } else if (ev.type === 'skill') this.release(spec, ev.srcId, ev.skillId, ev.tgtId);
    };
  }

  private onHit(ev: Extract<WorldEvent, { type: 'hit' }>, inner: (ev: WorldEvent) => void): void {
    const host = this.host;
    const pair = boutPair(host.world);
    const inBout = !!pair && pair.includes(ev.srcId) && pair.includes(ev.tgtId);
    // In a bout the small-hit rules stand (fx/duelHits.ts decides what shows).
    if (inBout) return inner(ev);
    const fx0 = host.fx.length;
    const parts0 = host.parts.length;
    inner(ev);
    if (ev.damage <= 0 || ev.outcome !== 'hit') return;
    // A plain landed blow: its mark is the sample's (its star on the foe); a foe other than the move's own gets nothing more.
    const added = host.fx.splice(fx0);
    for (const f of added) if (f.type === 'ghost') host.fx.push(f);
    host.parts.length = Math.min(host.parts.length, parts0);
  }

  private startCast(spec: PortSpec, srcId: number, skillId: string): void {
    const w = this.host.world;
    const src = w.get(srcId);
    if (!src) return;
    for (const p of this.plays) if (p.srcId === srcId) p.done = true;
    const tgt = w.get(src.cast?.targetId ?? src.targetId ?? null);
    const p = this.newPlay(spec, src, skillId, tgt && !tgt.dead ? tgt : null);
    p.phase = 'cast';
    p.c0 = w.time;
    p.windup = Math.max(0.05, src.cast?.windup ?? SKILLS[skillId]?.windup ?? 0.3);
    this.plays.push(p);
  }

  private release(spec: PortSpec, srcId: number, skillId: string, tgtId: number | null): void {
    const host = this.host;
    const w = host.world;
    const src = w.get(srcId);
    if (!src) return;
    const was = this.plays.find((p) => p.srcId === srcId && p.skillId === skillId && p.phase === 'cast' && !p.done);
    for (const p of this.plays) if (p.srcId === srcId && p !== was) p.done = true;
    let tgt = w.get(tgtId);
    if (!tgt || tgt.dead) {
      const own = w.get(src.targetId ?? null);
      const s = SKILLS[skillId];
      const reach = Math.max(3, s?.range ?? 0, s?.aoe ?? 0);
      tgt = own && !own.dead && Math.hypot(own.x - src.x, own.y - src.y) <= reach ? own : undefined;
    }
    const p = was ?? this.newPlay(spec, src, skillId, tgt ?? null);
    if (!was) this.plays.push(p);
    p.fromCast = !!was;
    p.phase = 'release';
    p.r0 = w.time;
    p.tgtId = tgt?.id ?? null;
    if (tgt) p.fW = [tgt.x, tgt.y];
    const a = host.anim(srcId);
    const sw = SKILL_SWINGS[a.style];
    p.contact = Math.max(0.05, (sw ? sw.contact : 0.3) * a.swingDur);
    const from = this.dashFrom.get(srcId);
    this.dashFrom.delete(srcId);
    if (spec.dash && from && SKILLS[skillId]?.dash) {
      p.aW = from;
      p.dashed = true;
    } else p.aW = [src.x, src.y];
    if (!tgt) {
      const fx = src.dirX || 1;
      const fy = src.dirY || 0;
      p.fW = [p.aW[0] + fx * 2.4, p.aW[1] + fy * 2.4];
    }
    const pair = boutPair(w);
    p.quiet = !!pair && pair.includes(srcId) && SKILLS[skillId]?.kind !== 'power';
    p.mirror = null;
  }

  private newPlay(spec: PortSpec, src: Entity, skillId: string, tgt: Entity | null): Play {
    const fW: [number, number] = tgt ? [tgt.x, tgt.y] : [src.x + (src.dirX || 1) * 2.4, src.y + (src.dirY || 0) * 2.4];
    const p: Play = {
      spec,
      srcId: src.id,
      skillId,
      tgtId: tgt?.id ?? null,
      phase: 'release',
      c0: 0,
      windup: 0,
      r0: 0,
      contact: 0.15,
      fromCast: false,
      aW: [src.x, src.y],
      fW,
      dashed: false,
      quiet: false,
      mirror: null,
      hist: [],
      ghostsLeft: [...(spec.ghosts ?? [])],
      bigDone: false,
      under: { c: null, step: -1, bx: 0, by: 0, res: 0 },
      over: { c: null, step: -1, bx: 0, by: 0, res: 0 },
      done: false,
    };
    return p;
  }

  /** The sample's clock now. */
  private clock(p: Play): number {
    const now = this.host.world.time;
    const s = p.spec;
    const rt = s.retime?.(p.srcId);
    if (rt !== null && rt !== undefined) return rt;
    if (p.phase === 'cast') {
      const [c0, c1] = s.cast!;
      return c0 + (c1 - c0) * Math.min(1, (now - p.c0) / p.windup);
    }
    const from = p.fromCast && s.cast ? s.cast[1] : s.pre;
    const u = now - p.r0;
    if (u < p.contact) return from + (s.hit - from) * (u / p.contact);
    return s.hit + (u - p.contact);
  }

  // ── The body ──

  /** Screen point of the caster's feet (no view offset), and its facing. */
  private feet(e: Entity): V {
    const [x, y] = this.host.pos(e);
    return [x, y - e.z];
  }

  private chest(e: Entity): number {
    return Math.max(12, Math.round(this.host.figH(e) * 0.5));
  }

  /** The weapon tip and hands from the frame the renderer drew (screen art px), or a stand-in at the chest. */
  private limbsNow(e: Entity): { hand: V; handB: V; tip: V } {
    const host = this.host;
    const [x, y] = this.feet(e);
    const sp = host.sprites.of(e);
    const face = host.iso.facing(e);
    const img = host.anim(e.id).lastSwing;
    const b = img ? bladesIn(img)[0] : undefined;
    if (b && sp) {
      const { set, facing } = viewOf(sp, host.iso.heading(e), face);
      const k = visualScale(e, set);
      const at = (bx: number, by: number): V => [x + facing * (bx - set.ax) * k, y + (by - set.ay) * k];
      return { hand: at(b.hx, b.hy), handB: at(b.hx - b.dx * 3, b.hy + 2), tip: at(b.hx + b.dx * b.len, b.hy + b.dy * b.len) };
    }
    const c = this.chest(e);
    const hand: V = [x + face * 9, y - c];
    return { hand, handB: [x + face * 4, y - c + 1], tip: [hand[0] + face * 2, hand[1]] };
  }

  private sampleLimbs(p: Play, src: Entity, T: number): void {
    const last = p.hist[p.hist.length - 1];
    const Ts = Math.floor(T * FPS * 2 + 1e-6) / (FPS * 2);
    if (last && last.T >= Ts) return;
    p.hist.push({ T: Ts, ...this.limbsNow(src) });
    if (p.hist.length > 24) p.hist.shift();
  }

  private leaveGhosts(p: Play, src: Entity, T: number): void {
    const host = this.host;
    while (p.ghostsLeft.length && T >= p.ghostsLeft[0]!) {
      const i = (p.spec.ghosts?.length ?? 0) - p.ghostsLeft.length;
      p.ghostsLeft.shift();
      const sp = host.sprites.of(src);
      if (!sp) continue;
      const { set, facing } = viewOf(sp, host.iso.heading(src), host.iso.facing(src));
      const [x, y] = this.feet(src);
      const [fx, fy] = host.iso.proj(p.fW[0], p.fW[1]);
      const d = unitOf([fx - x, fy - y]);
      const back = (p.spec.stepIn ?? 12) * (1 - (i + 1) / ((p.spec.ghosts?.length ?? 1) + 1));
      host.fx.push({
        type: 'ghost',
        step: true,
        x: x - d[0] * back,
        y: y - d[1] * back,
        t: 0,
        life: 0.4,
        img: host.anim(src.id).lastSwing ?? set.poses.dash ?? set.walk[1]!,
        flip: facing < 0,
        ax: set.ax,
        ay: set.ay,
      });
    }
  }

  // ── Painting ──

  /**
   * Draws every playing move's layer on the world picture itself (`g` = the pass frame's, never the effects' pen:
   * through the pen, or on the GPU path, `g.canvas` is the stand-in world canvas, and a 2D context taken from it is an
   * offscreen one the screen never shows; that is where batch 1's layers went).
   */
  drawLayer(g: CanvasRenderingContext2D, under: boolean, ox: number, oy: number): void {
    for (const p of this.plays) if (!p.done && (!under || p.spec.under)) this.paint(g, p, under, ox, oy);
  }

  private paint(g: CanvasRenderingContext2D, p: Play, under: boolean, ox: number, oy: number): void {
    if (p.done) return;
    const w = this.host.world;
    const src = w.get(p.srcId);
    if (!src) return;
    const T = Math.floor(this.clock(p) * FPS + 1e-6) / FPS;
    const step = Math.round(T * FPS);
    const raw = g;
    const tm = raw.getTransform();
    const res = Math.max(1, Math.round(Math.hypot(tm.a, tm.b)));
    const L = under ? p.under : p.over;
    if (L.step !== step || L.res !== res || !L.c) {
      L.step = step;
      L.res = res;
      L.c ??= takeCanvas(res);
      if (L.c.width !== BOX_W * res) {
        L.c.width = BOX_W * res;
        L.c.height = BOX_H * res;
      }
      this.render(L, p, src, T, under);
    }
    raw.save();
    raw.imageSmoothingEnabled = false;
    raw.globalAlpha = 1;
    raw.drawImage(L.c, 0, 0, L.c.width, L.c.height, L.bx + ox, L.by + oy, BOX_W, BOX_H);
    raw.restore();
  }

  private render(L: Layer, p: Play, src: Entity, T: number, under: boolean): void {
    const host = this.host;
    const c = L.c!;
    const cg = c.getContext('2d')!;
    cg.setTransform(1, 0, 0, 1, 0, 0);
    cg.clearRect(0, 0, c.width, c.height);
    const feet = this.feet(src);
    const a0 = host.iso.proj(p.aW[0], p.aW[1]);
    const aS: V = [Math.round(a0[0]), Math.round(a0[1])];
    const fS: V = host.iso.proj(p.fW[0], p.fW[1]);
    if (p.mirror === null) p.mirror = fS[0] < aS[0] - 0.5;
    const m = p.mirror;
    const mx = (q: V): V => (m ? [2 * aS[0] - q[0], q[1]] : q);
    const f = mx(fS);
    const d = unitOf(sub(f, aS));
    const chest = this.chest(src);
    // The box: round the caster and the foe, room above for the tall pieces.
    const midX = (aS[0] + fS[0]) / 2;
    const bx = Math.round(Math.min(Math.max(midX - BOX_W / 2, Math.min(aS[0], fS[0]) - 110), Math.max(aS[0], fS[0]) + 110 - BOX_W));
    const by = Math.round(Math.max(aS[1], fS[1]) + 44 - BOX_H);
    L.bx = bx;
    L.by = by;
    const r = L.res;
    // Art px -> cache px, mirrored about the caster when the foe is on the left.
    if (m) cg.setTransform(-r, 0, 0, r, (2 * aS[0] - bx) * r, -by * r);
    else cg.setTransform(r, 0, 0, r, -bx * r, -by * r);
    const live = mx(feet);
    const dist = Math.hypot(live[0] - aS[0], live[1] - aS[1]);
    const spec = p.spec;
    const body = (t: number): V => {
      if (!spec.move || !p.dashed) return live;
      const off = spec.move({ a: aS, f, d, chest }, t);
      const along = off[0] * d[0] + off[1] * d[1];
      const k = spec.dash ? dist / spec.dash : 1;
      return [aS[0] + d[0] * along * k + (off[0] - d[0] * along), aS[1] + d[1] * along * k + (off[1] - d[1] * along)];
    };
    const limbs = (t: number): { hand: V; handB: V; tip: V } => {
      let s = p.hist[p.hist.length - 1];
      for (let i = p.hist.length - 1; i >= 0; i--) {
        if (p.hist[i]!.T <= t + 1e-6) {
          s = p.hist[i]!;
          break;
        }
        s = p.hist[i]!;
      }
      const l = s ?? { ...this.limbsNow(src), T };
      return { hand: mx(l.hand), handB: mx(l.handB), tip: mx(l.tip) };
    };
    const scene: PortScene = {
      t: T,
      a: aS,
      f,
      d,
      chest,
      body,
      limbs,
      hands: (t) => {
        const l = limbs(t);
        return [l.hand, l.handB];
      },
      quiet: p.quiet,
      top: aS[1] - 104,
    };
    const pg = pen.begin(cg);
    if (under) spec.under?.(pg, scene);
    else spec.over(pg, scene);
    pen.end();
    cg.setTransform(1, 0, 0, 1, 0, 0);
    // The GPU path keeps a texture per picture: a repainted layer says so (render/webgl.ts TextureBank).
    touchCanvas(c);
  }
}

function unitOf(v: V): V {
  const l = Math.hypot(v[0], v[1]) || 1;
  return [v[0] / l, v[1] / l];
}

const runners = new WeakMap<object, Runner>();
let registered = false;
/** Hooks the runtime into the effect registry (once; every port file calls it). */
export function usePorts(): void {
  if (registered) return;
  registered = true;
  registerFx([], (base) => {
    const run = new Runner(base as Host);
    runners.set(base, run);
    return { step: () => run.step() };
  });
  // The layers draw from passes of their own, on the world picture (after the effects' passes: the ground layer over
  // the ground effects, the other over the figures and the effects).
  registerRenderPass({
    id: 'fxPortGround',
    layer: 'ground',
    order: 911,
    draw: (f) => runners.get(f.effects)?.drawLayer(f.g, true, f.ox, f.oy),
  });
  registerRenderPass({
    id: 'fxPortOver',
    layer: 'world',
    order: 301,
    draw: (f) => runners.get(f.effects)?.drawLayer(f.g, false, f.ox, f.oy),
  });
}
